import { Component, inject, OnInit } from '@angular/core';
import { AuthenticationFacade } from '../../facade/authentication.facade';
import { Router } from '@angular/router';
import { retry } from 'rxjs/operators';
import { timer } from 'rxjs';
import { NavigationFacade } from '../../../../shared/state/navigation/facades/navigation-facade';
import { NavigationService } from '../../../../shared/services/navigation.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { LoginService } from '../../services/login.service';
import { DatasetDrawerState } from '../../../datasets/components/dataset-drawer/drawer-state';
import { SessionTeardownService } from '../../../../shared/utils/clear-session-storage';
import { SessionFacade } from '../../../../shared/state/session/facades/session.facade';

@Component({
  selector: 'app-google-callback',
  standalone: true,
  template: `<p>Processing login...</p>`,
})
export class GoogleCallbackComponent implements OnInit {
  private readonly MAX_RETRIES = 3;
  private readonly INITIAL_RETRY_DELAY = 1000; // 1 second
  private navFacade = inject(NavigationFacade);
  private navService = inject(NavigationService);
  private logger = inject(LoggerService);
  private loginService = inject(LoginService);
  private datasetDrawerState = inject(DatasetDrawerState);
  private sessionFacade = inject(SessionFacade);
  private sessionTeardown = inject(SessionTeardownService);

  constructor(
    private authenticationFacade: AuthenticationFacade,
    private router: Router
  ) {}

  ngOnInit(): void {
    const allMenus = this.navService.getAllMenuItems();
    this.navFacade.setSideNavMenuItems(allMenus);

    setTimeout(() => {
      this.authenticationFacade.getAuthUser();
    }, 1000);

    this.authenticationFacade.user$
      .pipe(
        retry({
          count: this.MAX_RETRIES,
          delay: (error, retryCount) => timer(Math.min(this.INITIAL_RETRY_DELAY * Math.pow(2, retryCount), 10000)),
        })
      )
      .subscribe({
        next: (user) => {
          if (user && user.is_active === false) {
            this.logger.warn('User is inactive. Redirecting to login.', 'GoogleCallbackComponent');
            this.clearSessionAndRedirect();
          } else if (user) {
            this.router.navigate(['/dashboard']);
          } else {
            this.clearSessionAndRedirect();
          }
        },
        error: (err) => {
          this.logger.error('Authentication failed after maximum retries', 'GoogleCallbackComponent', err);
          this.clearSessionAndRedirect();
        },
      });
  }

  /**
   * Clears the session cookies and localStorage, then redirects to login.
   */
  private clearSessionAndRedirect(): void {
    this.loginService.postLogout().subscribe({
      complete: () => {
        this.sessionFacade.resetSessionState();
        this.sessionTeardown.clearStorage();
        this.datasetDrawerState.setSelectedSequenceFileIds([]);
        this.sessionTeardown.reloadToLogin();
      },
      error: () => {
        // Even if logout fails, drop the session state and leave the app
        this.sessionFacade.resetSessionState();
        this.sessionTeardown.clearStorage();
        this.datasetDrawerState.setSelectedSequenceFileIds([]);
        this.sessionTeardown.reloadToLogin();
      },
    });
  }
}
